import React, { ChangeEvent, useEffect } from "react";
import { InputAdornment } from "@mui/material";
import { FieldErrors } from "../../../domain/helpers/Errors";
import { BootstrapInput } from "../../../common/GlobalFunctions";
import { AssetInterface } from "../../../models/AssetModel/Asset";
import { useLanguageStore } from "../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import { darkModeBackgroudInputs } from "../../../utils/constants";
import Skeleton from "react-loading-skeleton";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select, { SingleValue } from "react-select";
import ToastMessage from "../../../common/ToastMessage";
import TextAreaCustom from "../../../common/TextAreaCustom";
import useCompany from "../../../store/ReactQueries/general/company/useCompany";
import useAssetTypes from "../../../store/ReactQueries/assets/assetTypes/useAssetTypes";
import useCustomerByCompId from "../../../store/ReactQueries/customers/useCustomerByCompId";
import useLocationsByCompId from "../../../store/ReactQueries/locations/useLocationByCompId";
import useAssetStatuses from "../../../store/ReactQueries/assets/assetStatuses/useAssetStatuses";
import useAssetCategories from "../../../store/ReactQueries/assets/assetCategories/useAssetCategories";
import useAssetDepartments from "../../../store/ReactQueries/assets/assetDepartments/useAssetDepartments";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import useVendors from "../../../store/ReactQueries/procurements/vendors/useVendors";
import dayjs from "dayjs";

export type AssetTypeInputs = "text" | "multitext" | "select" | "checkbox" | "date" | "photo" | "number" | "phone";

type HtmlEvent = HTMLInputElement | HTMLTextAreaElement;

interface CustomizedInputsStyledProps<T> {
  name: string;
  label: string;
  view: boolean;
  loading: boolean;
  errors: FieldErrors;
  form: AssetInterface;
  type: AssetTypeInputs;
  setForm: React.Dispatch<React.SetStateAction<AssetInterface>>;
}

const CustomizedInputsStyled = <T,>({
  label,
  type,
  name,
  form,
  errors,
  view,
  loading,
  setForm,
}: CustomizedInputsStyledProps<T>) => {
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();

  const { data: types, error: typesError } = useAssetTypes(0, 0);
  const { data: statuses, error: statusesError } = useAssetStatuses(0, 0);
  const { data: locations, error: locationsError } = useLocationsByCompId(0, 0, form.companyId);
  const { data: customers, error: customersError } = useCustomerByCompId(0, 0, form.companyId);
  const { data: categories, error: categoriesError } = useAssetCategories(0, 0);
  const { data: departments, error: departmentsError } = useAssetDepartments(0, 0);
  const { data: companies, error: companiesError } = useCompany(0, 0);
  const { data: vendors, error: vendorsError } = useVendors(0, 0);

  const dataErrors: (Error | null)[] = [
    typesError,
    statusesError,
    locationsError,
    categoriesError,
    departmentsError,
    companiesError,
    customersError,
    vendorsError,
  ];
  useEffect(() => {
    dataErrors.forEach((error) => {
      if (error) ToastMessage.Failed(`Error: ${error.message}`);
    });
  }, dataErrors);

  useEffect(() => {
    if (!form.companyId) {
      setForm((prevForm) => ({ ...prevForm, locationId: "", location: undefined }));
    }
  }, [form.companyId, name, setForm]);

  const renderData: any[] = React.useMemo(() => {
    switch (name) {
      case "assetCategoryId":
        return categories ?? [];
      case "assetTypeId":
        return types ?? [];
      case "locationId":
        return locations ?? [];
      case "assetDepartmentId":
        return departments ?? [];
      case "assetStatusId":
        return statuses ?? [];
      case "companyId":
        return companies ?? [];
      case "customerId":
        return customers ?? [];
      case "vendorId":
        return vendors ?? [];
      default:
        return [];
    }
  }, [name, categories, types, locations, departments, statuses, customers]);

  const handleText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    delete errors?.[name];
    setForm({ ...form, [name]: value });
  };

  const handleMultiSelect = (
    selectedOption: SingleValue<{
      label: any;
      value: string;
    }>
  ) => {
    delete errors?.[name];
    delete errors?.[name.slice(0, -2)];
    if (!selectedOption) {
      let { ...upForm } = form;
      upForm[name] = "";
      upForm[name.slice(0, -2)] = { title: "" };

      setForm(upForm);
      return;
    }
    let { ...upForm } = form;
    if (name === "companyId") {
      upForm.locationId = "";
      upForm.location = undefined;
      upForm.customer = undefined;
      upForm.customerId = undefined;
    }
    upForm[name] = selectedOption.value;
    upForm[name.slice(0, -2)] = { title: selectedOption.label };
    setForm(upForm);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    const { target } = e;
    let updatedValue = target.value;

    if (updatedValue.startsWith("0.")) {
    } else if (updatedValue === "0") updatedValue = "";
    else updatedValue = updatedValue.replace(/^0+(?!$)/, "");

    setForm((prevForm) => ({ ...prevForm, [target.id]: updatedValue }));
  };

  const handleDate = (e: ChangeEvent<HtmlEvent>) => {
    const { name, value } = e.target;
    delete errors?.[name];
    const res = value ? dayjs(value).format("YYYY-MM-DD") : undefined;
    setForm({ ...form, [name]: res });
  };

  const getErrorStyle = (errors: FieldErrors, name: string) => {
    return errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {};
  };

  const renderInput = () => {
    switch (type) {
      case "text":
        return (
          <BootstrapInput
            id={name}
            name={name}
            readOnly={view}
            value={form?.[name] ?? ""}
            darkmode={darkMode ? darkMode.toString() : "false"}
            sx={errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {}}
            onChange={handleText}
          />
        );
      case "phone":
        return (
          <div
            className={`mt-4 phone-field ${darkMode ? "dark-mode-input" : ""} ${errors?.[name] ? "has-error" : ""}`}
            style={view ? { pointerEvents: "none" } : {}}
          >
            <PhoneInput
              international
              defaultCountry="XK"
              value={form?.[name] ?? ""}
              onChange={(val) => {
                if (!view) setForm((p: any) => ({ ...p, [name]: val || "" }));
              }}
              placeholder="+383"
            />
          </div>
        );

      case "number":
        return (
          <BootstrapInput
            id={name}
            prefix="€"
            name={name}
            type="number"
            readOnly={view}
            value={form?.[name] ?? ""}
            darkmode={darkMode ? darkMode.toString() : "false"}
            sx={errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {}}
            startAdornment={
              <InputAdornment position="end" style={{ zIndex: 1, right: 35, position: "absolute" }}>
                €
              </InputAdornment>
            }
            onBlur={handleFocus}
            onFocus={handleFocus}
            onChange={handleText}
          />
        );
      case "multitext":
        return (
          <TextAreaCustom form={form} name={name} view={view} className="mt-4" errors={errors} setForm={setForm} />
        );
      case "select":
        return (
          <Select
            isDisabled={view}
            placeholder="Zgjedh"
            menuPlacement="top"
            menuPortalTarget={document.body}
            isClearable={name !== "companyId"}
            className={`mt-4 ${darkMode && "dark-mode-input"}`}
            value={{
              value: form?.[name],
              label:
                renderData?.find((item) => item.id === form?.[name])?.title ??
                renderData?.find((item) => item.id === form?.[name])?.name ??
                "",
            }}
            onChange={handleMultiSelect}
            options={renderData?.map(({ id, title, name }) => ({
              value: id ?? "",
              label: title ?? name ?? "",
            }))}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 11 }),
              container: (provided) => ({
                ...provided,
                position: "relative",
                zIndex: 11,
              }),
              singleValue: (provided) => ({
                ...provided,
                color: darkMode ? "white" : "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 11 }),
              control: (provided) => ({
                ...provided,
                backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
                borderColor: darkMode ? darkModeBackgroudInputs : "initial",
              }),
            }}
          />
        );
      case "date":
        return (
          <BootstrapInput
            darkmode={darkMode ? darkMode.toString() : "false"}
            value={form?.[name] ? dayjs(form[name]).format("YYYY-MM-DD") : form?.[name] ?? ""}
            id={name}
            name={name}
            type="date"
            sx={getErrorStyle(errors, name)}
            onChange={handleDate}
            readOnly={view}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormControl
      variant="standard"
      className={`${(type === "multitext" || name === "dateTime") && "modalMergeTwoRows"}`}
    >
      <InputLabel
        shrink
        htmlFor={name}
        sx={errors?.[name] ? { color: "#DC3545" } : { color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)" }}
      >
        {language[label]}
      </InputLabel>
      {loading ? <Skeleton className="mt-4" style={{ width: "100%", height: 41 }} /> : renderInput()}
      {errors?.[name] && (
        <p className="text-danger mb-0" style={{ fontSize: "12px" }}>
          {errors?.[name]}
        </p>
      )}
    </FormControl>
  );
};

export default CustomizedInputsStyled;
